import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { CONTACT_PREFS, formatPhone, formatPhoneInput, toE164 } from "../../lib/phone";
import { ContactPrefField, PhoneField } from "../auth/ContactFields";
import {
  colors,
  errorStyle,
  fieldStyle,
  inputStyle,
  labelStyle,
  linkButtonStyle,
  noticeStyle,
  primaryButtonStyle,
} from "../auth/authStyles";

const cardStyle = {
  backgroundColor: "white",
  borderRadius: "16px",
  padding: "1.75rem 2rem",
};

const rowStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  padding: "1rem 0",
  borderBottom: "1px solid #ededed",
};

const rowLabelStyle = { fontSize: "0.85rem", color: colors.muted };
const rowValueStyle = { fontSize: "1.05rem", fontWeight: 600, color: colors.black };

const ProfileTab = () => {
  const { user, profile, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [contactPref, setContactPref] = useState("text");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const provider = user.app_metadata?.provider === "google" ? "Google" : "Email";
  const prefLabel =
    CONTACT_PREFS.find((p) => p.value === profile?.contact_pref)?.label ?? "Text";

  const startEditing = () => {
    setFullName(profile?.full_name || "");
    setPhone(formatPhoneInput(formatPhone(profile?.phone)));
    setContactPref(profile?.contact_pref || "text");
    setError("");
    setNotice("");
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    const phoneE164 = toE164(phone);
    if (!phoneE164) {
      setError("Please enter a 10-digit mobile number, like (647) 555-0147.");
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        full_name: fullName.trim() || null,
        phone: phoneE164,
        contact_pref: contactPref,
      });
      setEditing(false);
      setNotice("Saved. We'll use these details for your orders.");
    } catch (err) {
      setError(err.message || "Couldn't save your details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <section
      className="account-section"
      style={{
        maxWidth: "720px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, margin: 0 }}>
            Contact details
          </h2>
          {!editing && (
            <button type="button" style={linkButtonStyle} onClick={startEditing}>
              Edit
            </button>
          )}
        </div>
        <p style={{ color: colors.muted, fontSize: "0.95rem", margin: "0.35rem 0 0.5rem" }}>
          How we reach you when your ball arrives or if anything comes up.
        </p>

        {notice && !editing && (
          <div style={{ ...noticeStyle, margin: "0.75rem 0" }} role="status">
            {notice}
          </div>
        )}

        {editing ? (
          <form
            onSubmit={handleSave}
            style={{ display: "flex", flexDirection: "column", gap: "1.1rem", paddingTop: "1rem" }}
          >
            {error && (
              <div style={errorStyle} role="alert">
                {error}
              </div>
            )}
            <div style={fieldStyle}>
              <label htmlFor="account-name" style={labelStyle}>
                Full name
              </label>
              <input
                id="account-name"
                type="text"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="auth-input"
                style={inputStyle}
              />
            </div>
            <PhoneField value={phone} onChange={setPhone} />
            <ContactPrefField value={contactPref} onChange={setContactPref} />
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
              <button
                type="submit"
                disabled={saving}
                style={{ ...primaryButtonStyle, width: "auto", padding: "0 1.75rem", opacity: saving ? 0.7 : 1 }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                style={{ ...linkButtonStyle, color: colors.muted }}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div style={rowStyle}>
              <span style={rowLabelStyle}>Name</span>
              <span style={rowValueStyle}>{profile?.full_name || "Not set"}</span>
            </div>
            <div style={rowStyle}>
              <span style={rowLabelStyle}>Mobile</span>
              <span style={rowValueStyle}>
                {profile?.phone ? formatPhone(profile.phone) : "Not set"}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={rowLabelStyle}>Best way to reach you</span>
              <span style={rowValueStyle}>{prefLabel}</span>
            </div>
            <div style={{ ...rowStyle, borderBottom: "none", paddingBottom: "0.25rem" }}>
              <span style={rowLabelStyle}>Email for receipts and updates</span>
              <span style={{ ...rowValueStyle, overflowWrap: "anywhere" }}>{user.email}</span>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          ...cardStyle,
          padding: "1.5rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={rowLabelStyle}>Signed in with</span>
          <span style={rowValueStyle}>{provider}</span>
        </div>
        <button
          type="button"
          onClick={handleSignOut}
          style={{
            height: "44px",
            padding: "0 1.4rem",
            fontSize: "0.95rem",
            fontWeight: 600,
            fontFamily: "inherit",
            borderRadius: "999px",
            border: `1px solid ${colors.border}`,
            backgroundColor: "white",
            cursor: "pointer",
          }}
        >
          Sign out
        </button>
      </div>
    </section>
  );
};

export default ProfileTab;
